import React, {useEffect, useState, useContext, useCallback, useMemo} from 'react';
import axios from 'axios';
import _ from 'lodash';
import {getDataTypeEndpointUrl} from './data-url-helpers';

function loadTypeDetail(dataTypeId, exclude) {
    let url = getDataTypeEndpointUrl(dataTypeId);
    let excludeStr = (exclude || []).join(',');
    return axios.get(`${url}/flattened`, {params: {exclude: excludeStr}}).then((response) => response.data);
}

export const TypeDetailContext = React.createContext(null);

export function WithTypeDetail(props) {
    const [typeState, setTypeState] = useState({});
    const [loading, setLoading] = useState([]);

    const loadType = useCallback(
        async (dataTypeId) => {
            if (!_.includes(loading, dataTypeId)) {
                setLoading((l) => [...l, dataTypeId]);
                await loadTypeDetail(dataTypeId, _.keys(typeState)).then((types) => {
                    setTypeState((oldTypeState) => {
                        let newTypeState = {
                            ...oldTypeState,
                            ..._.keyBy(types, 'dataTypeId'),
                        };
                        _.forIn(newTypeState, (dataType) => {
                            if (_.has(dataType, 'entityAttributes')) {
                                dataType.entityAttributes.forEach((attr) => {
                                    attr.dataType = newTypeState[attr.dataTypeId];
                                });
                            } else if (_.has(dataType, 'childDataTypeId')) {
                                dataType.childDataType = newTypeState[dataType.childDataTypeId];
                            } else if (_.has(dataType, 'entityDataTypeId')) {
                                dataType.entityDataType = newTypeState[dataType.entityDataTypeId];
                            }
                        });
                        return newTypeState;
                    });
                    setLoading((curLoading) => _.without(curLoading, dataTypeId));
                });
            }
        },
        [loading, setLoading, typeState, setTypeState],
    );

    const getType = useCallback(
        (dataTypeIdOrName) => {
            // old id, use legacy method.
            if (typeof dataTypeIdOrName === 'number' && !Number.isNaN(dataTypeIdOrName)) {
                return _.get(typeState, dataTypeIdOrName);
            }
            // otherwise lookup by dataType name.
            return _.find(typeState, {dataTypeName: dataTypeIdOrName});
        },
        [typeState],
    );

    const context = useMemo(
        () => ({
            loadedTypes: typeState,
            load: loadType,
            get: getType,
            loading,
        }),
        [typeState, loadType, getType, loading],
    );

    return <TypeDetailContext.Provider value={context}>{props.children}</TypeDetailContext.Provider>;
}

export function useTypeDetail(dataTypeId) {
    let typeDetailContext = useContext(TypeDetailContext);
    let typeDetail = typeDetailContext.get(dataTypeId);

    useEffect(() => {
        if (!typeDetail && dataTypeId && !typeDetailContext.loading.length) {
            typeDetailContext.load(dataTypeId);
        }
    }, [dataTypeId, typeDetail, typeDetailContext]);

    return typeDetail;
}

export function useLoadedTypes() {
    let typeDetailContext = useContext(TypeDetailContext);
    return typeDetailContext.loadedTypes;
}
